/** The Enter-key event fields the composer needs to decide how to handle it. */
export type ComposerEnterEvent = {
  key: string;
  shiftKey: boolean;
  isComposing: boolean;
};

/**
 * How the composer should handle a keydown:
 * - `ignore` — not a plain Enter (Shift+Enter for a newline, an IME composition,
 *   or any other key); let the textarea handle it.
 * - `blockWhileRunning` — a plain Enter while the agent is streaming; suppress it
 *   so a new request can't start until the run finishes or is stopped.
 * - `trySend` — a plain Enter while idle; run the send interceptor / default send.
 */
export type ComposerEnterAction = "ignore" | "blockWhileRunning" | "trySend";

/**
 * Decide how a composer keydown should be handled. Pure so the send/block gate
 * is unit-testable without an assistant-ui runtime.
 * @param event - The keydown's relevant fields.
 * @param isRunning - Whether the agent thread is currently streaming a run.
 * @returns The action the composer should take.
 */
export const resolveComposerEnter = (
  event: ComposerEnterEvent,
  isRunning: boolean
): ComposerEnterAction => {
  if (event.key !== "Enter" || event.shiftKey || event.isComposing) {
    return "ignore";
  }
  return isRunning ? "blockWhileRunning" : "trySend";
};
